import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Col, Button, Row as RowBootstrap } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import Alert from 'components/Common/Alert';
import conxtdOut from 'apis/conxtdOut';
import isEqual from "lodash/isEqual";

import Footer from './Footer';
import CustomSidePane from './CustomSidePane';

import './SDFormWrapper.scss';
import Icon from 'components/Icons/Icon';

function SDFormWrapper({
  showModal, 
  title,
  rowContent = [],
  keyboard = false,
  modalSize = "md",
  enableSidePane = false,
  sidePaneContent = {},
  handleClose = (() => { return }),
  submitForm = {},
  footerProps,
  setSubmitLoading,
  initialStateConditions = null, // populate an array of objects when passing as prop
}) {
  const darkMode = useSelector(state => state.app.darkMode);

  const dispatch = useDispatch();

  const [showSidePane, setShowSidePane] = useState(false);
  const [formData, setFormData] = useState({});
  const [formDataInitialised, setFormDataInitialised] = useState(false);
  const [triggerInitialiseForm, setTriggerInitialiseForm] = useState(false);
  const [notNull, setNotNull] = useState(false);
  const [initialFormState, setInitialFormState] = useState({});

  // Set initialFormState once formData is ready
  useEffect(() => {
    if (!formDataInitialised) return;

    const conditions = (initialStateConditions ?? []).reduce((acc, { field, value }) => {
      acc[field] = value;
      return acc;
    }, {});

    setInitialFormState({ ...formData, ...conditions });
  }, [formDataInitialised])

  // Mark formData as initialised once it has values
  useEffect(() => {
    if (formDataInitialised || Object.values(formData).length === 0) return;
    setFormDataInitialised(true);
  }, [formData, formDataInitialised])

  // Track unsaved changes
  useEffect(() => {
    if (!formDataInitialised) return;
    setNotNull(!isEqual(formData, initialFormState));
  }, [formData, initialFormState, formDataInitialised])

  const { 
    endpoint = null, 
    requestType = 'post', 
    exceptionalHandler = null,
    // dispatchOnSubmission = null,
    dispatchOnClose = { dispatchName: null, params: null },
    triggerOnSubmit = null,
    submitMessage = 'Are you sure you want to submit these changes?'
  } = submitForm;

  const handleSubmitForm = () => {
    Alert({
      text: submitMessage,
      icon: "warning",
      confirmButtonText: "Submit",
      showCancelButton: true,
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        setSubmitLoading(true);
        
        // if (dispatchOnSubmission) {

        // }

        // converts form data state object into multipart data FormElement
        const multiFormDataElement = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            multiFormDataElement.append(key, JSON.stringify(value));
          } else if (value !== null && typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
            multiFormDataElement.append(key, JSON.stringify(value));
          } else {
            multiFormDataElement.append(key, value);
          }
        });
      
        // submission to endpoint
        const submit = conxtdOut?.[requestType](endpoint, multiFormDataElement);
        
        submit
          .then(() => {
            setSubmitLoading(false);
            
            if (triggerOnSubmit) {
              triggerOnSubmit?.();
            }

            handleCloseModified();
            
            if (triggerInitialiseForm) { setTriggerInitialiseForm(false) };
            Alert({
              text: 'Submission Successful',
              icon: "success",
              timer: 3000,
            }).then(result => {
              if (result.value) {
                return;
              }
            })
          })

          .catch((error) => {
            setSubmitLoading(false);
            console.log('error: ', error);
            Alert({
              text: 'Something went wrong with this request. Please review your submission, try again later, or reach out to a member of our team',
              icon: "error",
              // confirmButtonText: "Submit",
              showCancelButton: true,
              cancelButtonText: "Dismiss",
              timer: 3000,
            }).then(result => {
              if (result.value) {
                return;
              }
            })
          })
      } else {
        return;
      }
    })
  }

  const handleCloseModified = useCallback(() => {
    handleClose?.();
    setShowSidePane(false);
    const { dispatchName, params } = dispatchOnClose;
    if (dispatchName) {
      dispatch(dispatchName({params}));
    }
  }, [handleClose, dispatch, dispatchOnClose])
  
  const toggleShowSidePane = () => {
    setShowSidePane(!showSidePane);
  }

  const checkClose = useCallback(() => {
    if (notNull) {
      Alert({
        text: `Close this window? Any unsaved changes will be lost.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Close",
        cancelButtonText: "Cancel",
      }).then(result => {
        if (result.value) {
          handleCloseModified();
          return;
        } else {
          return;
        }
      })
    } else {
      handleCloseModified();
      return;
    }
  }, [handleCloseModified, notNull])

  const mainRef = useRef(null);
  const observerRef = useRef(null);
  const [modalHeight, setModalHeight] = useState(0);

  useEffect(() => {
    if (!mainRef.current) return;
    
    observerRef.current = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height) {
        setModalHeight(prev => prev === height ? prev : height);
      }
    });

    observerRef.current.observe(mainRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [showModal]);

  return (
    <>
      <Modal
        backdrop='static'
        centered
        keyboard={keyboard}
        onHide={checkClose}
        show={showModal}
        size={enableSidePane ? 'xl' : modalSize ?? 'md'}
        id="sd-form-wrapper-modal"
        className={enableSidePane ? 'side-pane-layout' : ''}
      >
        <main id="sd-form-wrapper-modal-main">
          <Form
            className={`${title ? `${title}-sd-form` : ''} ${showSidePane ? 'col-8 h-100' : 'col-12'}`} 
            onSubmit={exceptionalHandler ?? handleSubmitForm ?? (() => { return })}
            ref={mainRef}
          >
            
            {/* Header */}
            <Modal.Header
              closeButton
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>

            {/* Body */}
            <Modal.Body
              className={`${darkMode ? 'darkMode' : 'background-modal-wrapper'}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className={`d-flex justify-content-center`}>
                <RowBootstrap className='w-100'>
                  <Col>

                    {/* Content - Rows */}
                    {rowContent?.map(({ component: Component, id, props }, index) => (
                      <Component
                        key={id}
                        id={id}
                        index={index}
                        {...props}
                        formData={formData}
                        setFormData={setFormData}
                        setFormDataInitialised={setFormDataInitialised}
                        triggerInitialiseForm={triggerInitialiseForm}
                      />
                    ))}
                    
                  </Col>
                </RowBootstrap>
              </div>
            </Modal.Body>

            {/* Footer */}
            <Modal.Footer className='px-0' style={{ maxHeight: '68px' }}>
              <Footer
                footerProps={{
                  ...footerProps,
                  handleSubmitForm,
                  handleClose: checkClose,
                }}
              />
            </Modal.Footer>
          </Form>

          {enableSidePane &&
            <>
              <Button onClick={() => toggleShowSidePane()} className={`toggle-side-pane-btn ${showSidePane ? 'active' : ''}`}>
                <span className='w-100 d-flex justify-content-center align-items-center'><Icon className='fas fa-info' /></span>
              </Button>
              <CustomSidePane showSidePane={showSidePane} sidePaneContent={sidePaneContent} modalHeight={modalHeight} />
            </>
          }

        </main>

      </Modal>

    </>    

    
  )
}

export default SDFormWrapper;
